<template>
  <USelect
    ref="select"
    v-bind="{ ...forwardedProps, ...$attrs }"
    :loading="false"
    :aria-busy="loading || undefined"
    class="p-select"
    @update:model-value="emit('update:modelValue', $event)"
    @update:open="emit('update:open', $event)"
    @change="emit('change', $event)"
    @blur="emit('blur', $event)"
    @focus="emit('focus', $event)"
  >
    <template v-if="slots.leading || leadingIcon" #leading="slotProps">
      <slot name="leading" v-bind="slotProps">
        <FontAwesomeIcon
          v-if="leadingIcon"
          :icon="leadingIcon"
          aria-hidden="true"
          data-slot="leadingIcon"
          :class="[
            slotProps.ui.leadingIcon({ class: props.ui?.leadingIcon }),
            { 'animate-spin': loading }
          ]"
        />
      </slot>
    </template>
    <template v-if="slots.default" #default="slotProps"><slot v-bind="slotProps" /></template>
    <template v-if="slots.trailing || trailingIcon" #trailing="slotProps">
      <slot name="trailing" v-bind="slotProps">
        <FontAwesomeIcon
          v-if="trailingIcon"
          :icon="trailingIcon"
          aria-hidden="true"
          data-slot="trailingIcon"
          :class="[
            slotProps.ui.trailingIcon({ class: props.ui?.trailingIcon, leading: isLeading }),
            { 'animate-spin': loading && !isLeading }
          ]"
        />
      </slot>
    </template>
    <template #item="slotProps">
      <slot name="item" v-bind="slotProps">
        <slot name="item-leading" v-bind="slotProps">
          <FontAwesomeIcon
            v-if="itemIcon(slotProps.item)"
            :icon="itemIcon(slotProps.item)!"
            aria-hidden="true"
            data-slot="itemLeadingIcon"
            :class="
              slotProps.ui.itemLeadingIcon({
                class: [props.ui?.itemLeadingIcon, itemObject(slotProps.item)?.ui?.itemLeadingIcon]
              })
            "
          />
          <UAvatar
            v-else-if="itemObject(slotProps.item)?.avatar"
            :size="
              (itemObject(slotProps.item)?.ui?.itemLeadingAvatarSize ||
                props.ui?.itemLeadingAvatarSize ||
                slotProps.ui.itemLeadingAvatarSize()) as AvatarProps['size']
            "
            v-bind="itemObject(slotProps.item)?.avatar"
            data-slot="itemLeadingAvatar"
            :class="
              slotProps.ui.itemLeadingAvatar({
                class: [
                  props.ui?.itemLeadingAvatar,
                  itemObject(slotProps.item)?.ui?.itemLeadingAvatar
                ]
              })
            "
          />
          <UChip
            v-else-if="itemObject(slotProps.item)?.chip"
            :size="
              (itemObject(slotProps.item)?.ui?.itemLeadingChipSize ||
                props.ui?.itemLeadingChipSize ||
                slotProps.ui.itemLeadingChipSize()) as ChipProps['size']
            "
            inset
            standalone
            v-bind="itemObject(slotProps.item)?.chip"
            data-slot="itemLeadingChip"
            :class="
              slotProps.ui.itemLeadingChip({
                class: [props.ui?.itemLeadingChip, itemObject(slotProps.item)?.ui?.itemLeadingChip]
              })
            "
          />
        </slot>
        <span
          data-slot="itemWrapper"
          :class="
            slotProps.ui.itemWrapper({
              class: [props.ui?.itemWrapper, itemObject(slotProps.item)?.ui?.itemWrapper]
            })
          "
        >
          <SelectItemText
            data-slot="itemLabel"
            :class="
              slotProps.ui.itemLabel({
                class: [props.ui?.itemLabel, itemObject(slotProps.item)?.ui?.itemLabel]
              })
            "
          >
            <slot name="item-label" :item="slotProps.item" :index="slotProps.index">
              {{
                itemObject(slotProps.item)
                  ? get(itemObject(slotProps.item), props.labelKey ?? "label")
                  : slotProps.item
              }}
            </slot>
          </SelectItemText>
          <span
            v-if="
              itemObject(slotProps.item) &&
              (get(itemObject(slotProps.item), props.descriptionKey ?? 'description') ||
                slots['item-description'])
            "
            data-slot="itemDescription"
            :class="
              slotProps.ui.itemDescription({
                class: [props.ui?.itemDescription, itemObject(slotProps.item)?.ui?.itemDescription]
              })
            "
          >
            <slot name="item-description" :item="slotProps.item" :index="slotProps.index">
              {{ get(itemObject(slotProps.item), props.descriptionKey ?? "description") }}
            </slot>
          </span>
        </span>
        <span
          data-slot="itemTrailing"
          :class="
            slotProps.ui.itemTrailing({
              class: [props.ui?.itemTrailing, itemObject(slotProps.item)?.ui?.itemTrailing]
            })
          "
        >
          <slot name="item-trailing" v-bind="slotProps" />
          <SelectItemIndicator as-child>
            <FontAwesomeIcon
              :icon="(selectedIcon || faCheck) as IconProp"
              aria-hidden="true"
              data-slot="itemTrailingIcon"
              :class="
                slotProps.ui.itemTrailingIcon({
                  class: [
                    props.ui?.itemTrailingIcon,
                    itemObject(slotProps.item)?.ui?.itemTrailingIcon
                  ]
                })
              "
            />
          </SelectItemIndicator>
        </span>
      </slot>
    </template>
    <template v-if="slots['content-top']" #content-top="slotProps"
      ><slot name="content-top" v-bind="slotProps"
    /></template>
    <template v-if="slots['content-bottom']" #content-bottom="slotProps"
      ><slot name="content-bottom" v-bind="slotProps"
    /></template>
  </USelect>
</template>

<script
  setup
  lang="ts"
  generic="
    T extends ArrayOrNested<SelectItem> = ArrayOrNested<SelectItem>,
    VK extends GetItemKeys<T> = 'value',
    M extends boolean = false,
    Mod extends Omit<ModelModifiers, 'lazy'> = Omit<ModelModifiers, 'lazy'>
  "
>
import { computed, useTemplateRef } from "vue"
import NuxtSelect, {
  type SelectItem,
  type SelectEmits,
  type SelectSlots
} from "@nuxt/ui/components/Select.vue"
import type { ArrayOrNested, GetItemKeys, ModelModifiers } from "@nuxt/ui"
import type { ComponentExposed } from "vue-component-type-helpers"
import { faCheck, faChevronDown, faSpinner } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { get } from "@nuxt/ui/utils"
import { SelectItemIndicator, SelectItemText } from "reka-ui"
import UAvatar, { type AvatarProps } from "@nuxt/ui/components/Avatar.vue"
import UChip, { type ChipProps } from "@nuxt/ui/components/Chip.vue"
import type { PSelectProps } from "./select"

const USelect = NuxtSelect<T, VK, M, Mod>
const props = withDefaults(defineProps<PSelectProps<T, VK, M, Mod>>(), {
  color: "secondary",
  variant: "none",
  open: undefined,
  defaultOpen: undefined,
  multiple: undefined,
  portal: undefined,
  arrow: undefined,
  disabled: undefined,
  required: undefined,
  highlight: undefined,
  fixed: undefined,
  autofocus: undefined,
  leading: undefined,
  trailing: undefined,
  loading: undefined
})
const emit = defineEmits<SelectEmits<T, VK, M, Mod>>()
const slots = defineSlots<SelectSlots<T, VK, M, Mod>>()
const forwardedProps = computed(() => ({
  ...props,
  icon: undefined,
  leadingIcon: undefined,
  trailingIcon: undefined,
  loadingIcon: undefined,
  selectedIcon: undefined
}))
const isLeading = computed(() =>
  Boolean(
    props.leadingIcon ||
    (props.icon && (props.leading || !props.trailing)) ||
    (props.loading && !props.trailing)
  )
)
const leadingIcon = computed(() =>
  isLeading.value
    ? ((props.loading
        ? props.loadingIcon || faSpinner
        : props.leadingIcon || props.icon) as IconProp)
    : undefined
)
const trailingIcon = computed(() =>
  props.trailing !== false
    ? ((props.loading && !isLeading.value
        ? props.loadingIcon || faSpinner
        : props.trailingIcon || (props.trailing && props.icon) || faChevronDown) as IconProp)
    : undefined
)
function itemObject(item: SelectItem) {
  return typeof item === "object" && item !== null ? item : undefined
}
function itemIcon(item: SelectItem) {
  return itemObject(item)?.icon as IconProp | undefined
}
const select = useTemplateRef<ComponentExposed<typeof USelect>>("select")
defineOptions({ inheritAttrs: false })
defineExpose({
  get triggerRef() {
    return select.value!.triggerRef
  },
  get viewportRef() {
    return select.value!.viewportRef
  }
})
</script>
